import SecurityIcon from "@mui/icons-material/Security";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExitToAppIcon from "@mui/icons-material/ExitToAppOutlined";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { t } from "i18next";
import { Secret, TOTP } from "otpauth";
import { QRCodeCanvas } from "qrcode.react";
import { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { Copyright } from "../Copyright";
import { useAuth } from "./ParseAuthContext.tsx";

const SecurityAvatar = styled(Avatar)`
  background-color: ${(props) => props.theme.palette.secondary.main};
  margin: ${(props) => props.theme.spacing(1)};
`;

const ISSUER = "CaseSolv";

function buildOtpAuthUri(username: string, secret: string): string {
  const totp = new TOTP({
    issuer: ISSUER,
    label: username,
    algorithm: "SHA1",
    digits: 6,
    period: 30,
    secret: Secret.fromBase32(secret),
  });
  return totp.toString();
}

type Phase = "scan" | "recovery";

export default function MfaEnrolment({ username }: { username: string }) {
  const { theme } = useTheme();
  const { enrolMfa, confirmMfa, logout } = useAuth();

  const secret = useMemo(() => new Secret({ size: 20 }).base32, []);
  const otpUri = useMemo(
    () => buildOtpAuthUri(username, secret),
    [username, secret],
  );

  const [phase, setPhase] = useState<Phase>("scan");
  const [token, setToken] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleConfirm() {
    setSubmitting(true);
    setError("");
    try {
      const { recoveryCodes } = await enrolMfa(secret, token.trim());
      if (recoveryCodes.length === 0) {
        throw new Error("Server did not return recovery codes");
      }
      setRecoveryCodes(recoveryCodes);
      setPhase("recovery");
    } catch (err) {
      console.warn("MFA enrolment failed", err);
      setError(t("mfa.enrolment.error.confirmFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAcknowledge() {
    setConfirming(true);
    setError("");
    try {
      await confirmMfa();
    } catch (err) {
      console.warn("MFA confirmation failed", err);
      setError(t("mfa.enrolment.error.confirmFailed"));
    } finally {
      setConfirming(false);
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    } catch (err) {
      console.warn("Clipboard copy failed", err);
    }
  }

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
    } catch (err) {
      console.warn("Failed to log out", err);
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
        gap: theme.spacing(2),
        py: theme.spacing(6),
      }}
    >
      <Stack
        sx={{
          gap: theme.spacing(3),
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: theme.spacing(1),
              alignItems: "center",
            }}
          >
            <SecurityAvatar>
              <SecurityIcon />
            </SecurityAvatar>
            <Typography component="h1" variant="h5">
              <Trans i18nKey="mfa.enrolment.title" />
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={<ExitToAppIcon />}
            onClick={handleLogout}
            loading={loggingOut}
            disabled={submitting || confirming}
          >
            <Trans i18nKey="mfa.enrolment.logout" />
          </Button>
        </Box>

        {phase === "scan" && (
          <Paper sx={{ p: theme.spacing(3) }}>
            <Stack
              sx={{
                gap: theme.spacing(2),
              }}
            >
              <Typography>
                <Trans i18nKey="mfa.enrolment.intro" />
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  my: theme.spacing(1),
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    p: theme.spacing(2),
                    borderRadius: 1,
                  }}
                >
                  <QRCodeCanvas value={otpUri} size={200} />
                </Box>
              </Box>

              <Stack
                sx={{
                  gap: theme.spacing(0.5),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  <Trans i18nKey="mfa.enrolment.manualSecretLabel" />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    backgroundColor: theme.palette.action.hover,
                    p: theme.spacing(1),
                    borderRadius: 1,
                  }}
                >
                  {secret}
                </Typography>
              </Stack>

              <TextField
                label={<Trans i18nKey="mfa.enrolment.tokenLabel" />}
                value={token}
                onChange={(e) =>
                  setToken(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                }
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                  },
                }}
                disabled={submitting}
                autoFocus
                fullWidth
              />

              {error !== "" && (
                <Alert severity="error">
                  <AlertTitle>
                    <Trans i18nKey="mfa.enrolment.error.title" />
                  </AlertTitle>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                loading={submitting}
                disabled={token.length !== 6}
                onClick={handleConfirm}
                fullWidth
              >
                <Trans i18nKey="mfa.enrolment.confirm" />
              </Button>
            </Stack>
          </Paper>
        )}

        {phase === "recovery" && (
          <Paper sx={{ p: theme.spacing(3) }}>
            <Stack
              sx={{
                gap: theme.spacing(2),
              }}
            >
              <Alert severity="warning">
                <AlertTitle>
                  <Trans i18nKey="mfa.enrolment.recovery.warningTitle" />
                </AlertTitle>
                <Trans i18nKey="mfa.enrolment.recovery.warningBody" />
              </Alert>

              <Stack
                sx={{
                  gap: theme.spacing(1),
                }}
              >
                {recoveryCodes.map((code, idx) => (
                  <Typography
                    key={idx}
                    variant="body1"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: theme.palette.action.hover,
                      p: theme.spacing(1.5),
                      borderRadius: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {code}
                  </Typography>
                ))}
              </Stack>

              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyRecoveryCodes}
              >
                <Trans i18nKey="mfa.enrolment.recovery.copy" />
              </Button>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                }
                label={<Trans i18nKey="mfa.enrolment.recovery.acknowledge" />}
              />

              {error !== "" && (
                <Alert severity="error">
                  <AlertTitle>
                    <Trans i18nKey="mfa.enrolment.error.title" />
                  </AlertTitle>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                disabled={!acknowledged}
                loading={confirming}
                onClick={handleAcknowledge}
                fullWidth
              >
                <Trans i18nKey="mfa.enrolment.recovery.continue" />
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
      <Copyright />
    </Container>
  );
}
